import type { RuleConfig, SessionFormat } from './types.js';

/** Indian Rummy, 13 cards, two decks — the single configuration v1 ships. */
export const INDIAN_RUMMY_13: RuleConfig = {
  id: 'indian-rummy-13',
  deckCount: 2,
  printedJokers: 2,
  handSize: 13,
  minSequences: 2,
  minPureSequences: 1,
  minMeldSize: 3,
  maxSetSize: 4,
  aceLow: true,
  aceHigh: true,
  roundTheCorner: false,
  wildCardsMayPlayNatural: true,
  allowAllWildMeld: true,
  mayDiscardDrawnOpenCard: false,
  wildOnPrintedJokerCut: 'A',
  scoring: {
    highCard: 10,
    joker: 0,
    cap: 80,
    losingFloor: 2,
    invalidDeclaration: 80,
    firstDrop: 20,
    middleDrop: 40,
  },
};

/**
 * Two players, one deck.
 *
 * The design document's §3.1 table specifies two decks at every table size.
 * That is a defensible choice — it keeps a single configuration — but it is not
 * how two people play rummy at a table. With 106 cards and 28 dealt, the closed
 * deck holds 78; the draw is so deep that neither player can read the other and
 * a hand becomes a solitaire race. One deck plus one printed joker leaves 25 in
 * the stock, which is the game two people actually play.
 *
 * This is a deliberate deviation from the document, and it is exactly the kind
 * of variance §10 asks the engine to express as configuration rather than a
 * rewrite.
 */
export const INDIAN_RUMMY_13_SINGLE_DECK: RuleConfig = {
  ...INDIAN_RUMMY_13,
  id: 'indian-rummy-13-1d',
  deckCount: 1,
  printedJokers: 1,
};

/**
 * Seven seats and up, three decks.
 *
 * Deck size is fixed but the deal is not, so past some seat count two decks
 * stop being enough: at seven seats they put 91 of 106 cards into hands,
 * leaving *13* in the stock — under two draws each before the discard pile is
 * reshuffled back in. That is not a rummy hand, it is a shuffle loop, and a
 * recycled stock is one every attentive player has already seen, which quietly
 * changes the information game.
 *
 * Six seats is the judgement call, and it goes to two decks. The stock there is
 * 26 — about four draws each, thin enough that a long hand will recycle once —
 * but two decks is what Indian Rummy *is* to the people playing it, and a table
 * that deals an unfamiliar number of jokers is a worse surprise than a stock
 * that occasionally turns over. Deep stock loses to familiarity at the boundary;
 * it only wins once two decks stop working at all.
 *
 * Three copies of a card in circulation is safe by construction: a set is
 * distinct *suits* (`classifySet`), so the third 7♠ can no more join a set of
 * sevens than the second could, and `maxSetSize` still caps a set at four.
 */
export const INDIAN_RUMMY_13_TRIPLE_DECK: RuleConfig = {
  ...INDIAN_RUMMY_13,
  id: 'indian-rummy-13-3d',
  deckCount: 3,
  printedJokers: 3,
};

/**
 * Table configuration by seat count (§3.1, amended as above).
 *
 * Rooms currently clamp to six seats, so the three-deck branch is unreachable
 * in practice — it is here so that raising the cap does not silently deal a
 * seven-handed table a thirteen-card stock.
 */
export function configForSeats(seats: number): RuleConfig {
  if (seats <= 2) return INDIAN_RUMMY_13_SINGLE_DECK;
  return seats >= 7 ? INDIAN_RUMMY_13_TRIPLE_DECK : INDIAN_RUMMY_13;
}

/** How many decks a table of this size plays with, without building one. */
export function decksForSeats(seats: number): number {
  return configForSeats(seats).deckCount;
}

/**
 * Configs are resolved by id when a hand is replayed from the event log, so a
 * hand dealt under one configuration never rehydrates under another.
 */
export const RULE_CONFIGS: Record<string, RuleConfig> = {
  [INDIAN_RUMMY_13.id]: INDIAN_RUMMY_13,
  [INDIAN_RUMMY_13_SINGLE_DECK.id]: INDIAN_RUMMY_13_SINGLE_DECK,
  [INDIAN_RUMMY_13_TRIPLE_DECK.id]: INDIAN_RUMMY_13_TRIPLE_DECK,
};

export function configById(id: string | undefined): RuleConfig | null {
  return id ? (RULE_CONFIGS[id] ?? null) : null;
}

/**
 * Gin Rummy is not in v1 — it is here only to keep the configuration surface
 * honest. If the engine cannot express it, the config is not really a config.
 */
export const GIN_RUMMY_10: RuleConfig = {
  ...INDIAN_RUMMY_13,
  id: 'gin-rummy-10',
  deckCount: 1,
  printedJokers: 0,
  handSize: 10,
  minSequences: 0,
  minPureSequences: 0,
  aceHigh: false,
  wildCardsMayPlayNatural: true,
  allowAllWildMeld: false,
};

export const FORMAT_META: Record<
  SessionFormat,
  { kind: 'deals' | 'pool'; deals?: number; threshold?: number; reentry: boolean }
> = {
  'deals-2': { kind: 'deals', deals: 2, reentry: false },
  'deals-3': { kind: 'deals', deals: 3, reentry: false },
  'deals-6': { kind: 'deals', deals: 6, reentry: false },
  'pool-101': { kind: 'pool', threshold: 101, reentry: false },
  'pool-201': { kind: 'pool', threshold: 201, reentry: true },
};
